import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Deck } from '../types/pokemon';
import { useAuth } from './useAuth';

export function useDecks() {
  const [decks, setDecks] = useState<(Deck & { 
    rating_score?: number;
    total_votes?: number;
  })[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const fetchDecks = async () => {
    if (!user) return;

    try {
      setLoading(true);
      
      const { data: ratingsData } = await supabase
        .from('deck_ratings')
        .select('*');

      const ratingsMap = new Map(
        ratingsData?.map(rating => [rating.deck_id, rating]) || []
      );

      const { data, error: decksError } = await supabase
        .from('decks')
        .select(`
          *,
          profiles:user_id (
            username,
            avatar_url
          ),
          cards:deck_cards(
            quantity,
            card:cards(*)
          )
        `)
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });

      if (decksError) throw decksError;
      
      const processedDecks = (data || []).map(deck => {
        const rating = ratingsMap.get(deck.id);
        return {
          ...deck,
          rating_score: rating?.rating_score || 0,
          total_votes: rating?.total_votes || 0,
          totalCards: deck.cards?.reduce((sum, card) => sum + card.quantity, 0) || 0
        };
      });
      
      setDecks(processedDecks);
    } catch (err) {
      console.error('Error fetching decks:', err);
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const createDeck = async (name: string, description: string = '', isPublic: boolean = false) => {
    if (!user) return null;

    try {
      const { data, error } = await supabase
        .from('decks')
        .insert({
          name,
          description,
          user_id: user.id,
          is_public: isPublic
        })
        .select()
        .single();

      if (error) throw error;
      
      setDecks(prev => [{ ...data, rating_score: 0, total_votes: 0 }, ...prev]);
      return data;
    } catch (err) {
      console.error('Error creating deck:', err);
      throw err;
    }
  };

  const deleteDeck = async (deckId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('decks')
        .delete()
        .eq('id', deckId)
        .eq('user_id', user.id);

      if (error) throw error;
      
      setDecks(prev => prev.filter(deck => deck.id !== deckId));
    } catch (err) {
      console.error('Error deleting deck:', err);
      throw err;
    }
  };

  const updateDeck = async (deckId: string, updates: { name?: string; description?: string; is_public?: boolean }) => {
    try {
      const { error } = await supabase
        .from('decks')
        .update(updates)
        .eq('id', deckId);

      if (error) throw error;
      
      setDecks(prev =>
        prev.map(deck =>
          deck.id === deckId ? { ...deck, ...updates } : deck
        )
      );
    } catch (err) {
      console.error('Error updating deck:', err);
      throw err;
    }
  };

  useEffect(() => {
    if (user) {
      fetchDecks();
    }
  }, [user]);

  return {
    decks,
    loading,
    error,
    createDeck,
    updateDeck,
    deleteDeck,
    refresh: fetchDecks,
  };
}